import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Firestore, doc, onSnapshot, setDoc, updateDoc, getDoc, deleteDoc, Unsubscribe } from '@angular/fire/firestore';
import { AuthService } from './auth.service';
import { ToastrService } from 'ngx-toastr';

export interface CallData {
  offer?: RTCSessionDescriptionInit;
  answer?: RTCSessionDescriptionInit;
  candidates?: RTCIceCandidateInit[];
  providerEmail?: string;
  clientEmail?: string;
  clientUid?: string;
  providerUid?: string;
  status?: 'incoming' | 'accepted' | 'rejected' | 'ended';
  timestamp?: number;
}

@Injectable({
  providedIn: 'root'
})
export class CallService {
  private callActiveSubject = new BehaviorSubject<boolean>(false);
  public callActive$ = this.callActiveSubject.asObservable();

  private callDataSubject = new BehaviorSubject<CallData | null>(null);
  public callData$ = this.callDataSubject.asObservable();

  private callListenerUnsubscribe: Unsubscribe | null = null;
  private currentUserId: string = '';

  constructor(
    private firestore: Firestore,
    private authService: AuthService,
    private toastr: ToastrService,
    private zone: NgZone
  ) {
    this.authService.userSubject.subscribe(user => {
      this.zone.run(() => {
        if (user) {
          this.currentUserId = user.uid;
          this.listenToCallDocument(this.currentUserId); // Listen for incoming calls
        } else {
          this.cleanup(); // User logged out
        }
      });
    });
  }

  private listenToCallDocument(docId: string): void {
    console.log(`Setting up call listener for document: calls/${docId}`);

    // Stop any existing listener
    if (this.callListenerUnsubscribe) {
      this.callListenerUnsubscribe();
    }

    const callDocRef = doc(this.firestore, `calls/${docId}`);

    this.callListenerUnsubscribe = onSnapshot(callDocRef, (snapshot) => {
      this.zone.run(() => {
        if (snapshot.exists()) {
          const callData = snapshot.data() as CallData;
          console.log('Call data received:', callData);

          this.callDataSubject.next(callData);
          const isActive = callData.status === 'accepted' || callData.status === 'incoming';
          this.callActiveSubject.next(isActive);

          // Handle status changes
          this.handleCallStatus(callData, docId);
        } else {
          // Document deleted, meaning call is over
          console.log('Call document does not exist, cleaning up state.');
          this.resetCallState();
          // If we were listening to a specific call, revert to listening for our own calls
          if (docId !== this.currentUserId) {
            this.listenToCallDocument(this.currentUserId);
          }
        }
      });
    });
  }
  
  private handleCallStatus(callData: CallData, docId: string): void {
    const isProvider = callData.providerUid === this.currentUserId;
    const isClient = callData.clientUid === this.currentUserId;
  
    if (callData.status === 'ended' || callData.status === 'rejected') {
      if (isProvider) {
        this.toastr.info(callData.status === 'rejected' ? 'Call rejected' : 'Call ended');
      }
      // Short delay to allow all clients to receive the final status
      setTimeout(() => {
        this.resetCallState();
        // The user who initiated the call (provider) needs to delete the document
        if (isProvider) {
          deleteDoc(doc(this.firestore, `calls/${docId}`));
        }
        // Everyone should revert to listening to their own call document
        this.listenToCallDocument(this.currentUserId);
      }, 1500);
    } else if (callData.status === 'accepted' && isProvider) {
      this.toastr.success('Call accepted');
    }
  }

  public async initiateCall(receiverUid: string, receiverEmail: string): Promise<boolean> {
    if (!this.currentUserId || !receiverUid) {
      this.toastr.error('Cannot make a call. User not logged in.', 'Error');
      return false;
    }

    const callDocRef = doc(this.firestore, `calls/${receiverUid}`);
    const callSnapshot = await getDoc(callDocRef);
    if (callSnapshot.exists() && (callSnapshot.data() as CallData).status !== 'ended') {
      this.toastr.warning('User is in another call.', 'Busy');
      return false;
    }
    
    const currentUser = this.authService.userSubject.value;
    const callData: CallData = {
      providerUid: this.currentUserId,
      providerEmail: currentUser?.email || '',
      clientUid: receiverUid,
      clientEmail: receiverEmail,
      status: 'incoming',
      timestamp: Date.now()
    };

    try {
      await setDoc(callDocRef, callData);
      // Switch listener to the new call document
      this.listenToCallDocument(receiverUid);
      this.callDataSubject.next(callData);
      this.callActiveSubject.next(true);
      return true;
    } catch (error) {
      console.error('Error initiating call:', error);
      this.toastr.error('Could not initiate call', 'Error');
      return false;
    }
  }

  public async updateCallData(data: Partial<CallData>, docId?: string): Promise<void> {
    const callDocId = docId || this.getCurrentCallData()?.clientUid;
    if (!callDocId) return;

    const callDocRef = doc(this.firestore, `calls/${callDocId}`);
    try {
      if (data.candidates) {
        const snap = await getDoc(callDocRef);
        const existingCandidates = snap.exists() ? (snap.data() as CallData).candidates || [] : [];
        data.candidates = [...existingCandidates, ...data.candidates];
      }
      await updateDoc(callDocRef, data);
    } catch (error) {
      console.error('Error updating call data:', error);
    }
  }

  public async endCall(docId?: string): Promise<void> {
    const callDocId = docId || this.getCurrentCallData()?.clientUid;
    if (!callDocId) {
      console.log('Could not determine call document to end.');
      return;
    }

    const callDocRef = doc(this.firestore, `calls/${callDocId}`);
    try {
      const docSnapshot = await getDoc(callDocRef);
      if (docSnapshot.exists()) {
        await updateDoc(callDocRef, { status: 'ended' });
      }
    } catch (error) {
      console.error('Error ending call:', error);
    }
  }

  private resetCallState(): void {
    this.callActiveSubject.next(false);
    this.callDataSubject.next(null);
  }

  public getCurrentCallData(): CallData | null {
    return this.callDataSubject.value;
  }

  private cleanup(): void {
    if (this.callListenerUnsubscribe) {
      this.callListenerUnsubscribe();
      this.callListenerUnsubscribe = null;
    }
    this.resetCallState();
    this.currentUserId = '';
  }
}